// Detailed weather data for the weather-nerd widgets: NWS gridpoint forecast
// series, observation extras, Storm Prediction Center outlooks and the local
// forecast office's discussion. All free U.S. government sources.

import type { SourceStamp } from "./models";

/** One hour of the NWS gridpoint forecast (metric; the UI converts). */
export interface GridHour {
  t: string;
  tempC: number | null;
  dewC: number | null;
  feelsC: number | null;
  rh: number | null;
  sky: number | null;
  windKph: number | null;
  gustKph: number | null;
  windDeg: number | null;
  pop: number | null;
  /** Liquid precipitation expected in this hour (mm). */
  qpfMm: number | null;
  snowMm: number | null;
  iceMm: number | null;
  thunderPct: number | null;
}

export interface FireIndices {
  /** Haines index max over the next 24 h (2–6; 5–6 = dry, unstable air that lets fires blow up). */
  hainesMax: number | null;
  /** NWS grassland fire danger index max over 24 h (1 low … 5 extreme), where the office issues it. */
  grasslandMax: number | null;
  minRh: number | null;
  maxGustKph: number | null;
  mixingHeightMaxM: number | null;
}

export interface GridPayload {
  hours: GridHour[];
  fire: FireIndices;
  updatedAt: string | null;
  stamp: SourceStamp;
}

export interface CloudLayer {
  baseM: number | null;
  /** METAR cover: FEW, SCT, BKN, OVC, CLR, SKC, VV. */
  amount: string;
}

export interface ObsDetail {
  stationId: string;
  observedAt: string | null;
  cloudLayers: CloudLayer[];
  pressureHpa: number | null;
  /** Change over about the last three hours. */
  pressureChange3hHpa: number | null;
  tendency: "rising" | "falling" | "steady" | null;
  precipLastHourMm: number | null;
  precipLast6hMm: number | null;
  high24hC: number | null;
  low24hC: number | null;
  stamp: SourceStamp;
}

export type OutlookLabel = "TSTM" | "MRGL" | "SLGT" | "ENH" | "MDT" | "HIGH";

export interface OutlookDay {
  day: 1 | 2 | 3;
  /** null = no thunderstorms expected at home. */
  label: OutlookLabel | null;
  validFrom: string | null;
  validTo: string | null;
}

export interface OutlookPayload {
  days: OutlookDay[];
  stamp: SourceStamp;
}

export interface DiscussionSection {
  title: string;
  text: string;
}

export interface DiscussionPayload {
  office: string;
  issuedAt: string | null;
  sections: DiscussionSection[];
  stamp: SourceStamp;
}

export const OUTLOOK_INFO: Record<OutlookLabel, { level: number; name: string; color: string; meaning: string }> = {
  TSTM: { level: 0, name: "Thunderstorms", color: "#bfe7bf", meaning: "Thunderstorms possible, not expected to be severe." },
  MRGL: { level: 1, name: "Marginal", color: "#66a366", meaning: "Isolated severe storms possible: limited in duration, coverage or intensity." },
  SLGT: { level: 2, name: "Slight", color: "#f6f67b", meaning: "Scattered severe storms possible: short-lived, not widespread." },
  ENH: { level: 3, name: "Enhanced", color: "#e6c27a", meaning: "Numerous severe storms possible: more persistent and widespread, a few intense." },
  MDT: { level: 4, name: "Moderate", color: "#e67f7f", meaning: "Widespread severe storms likely: long-lived and intense." },
  HIGH: { level: 5, name: "High", color: "#ff66ff", meaning: "Widespread, long-lived, particularly intense severe storms expected." },
};

/** SPC categorical outlook DN values. */
export const OUTLOOK_BY_DN: Record<number, OutlookLabel> = { 2: "TSTM", 3: "MRGL", 4: "SLGT", 5: "ENH", 6: "MDT", 8: "HIGH" };

/** Ray-casting point-in-polygon on [lon, lat] rings (outer ring, then holes). */
export function inPolygon(lon: number, lat: number, rings: number[][][]): boolean {
  const inRing = (ring: number[][]) => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i] as [number, number];
      const [xj, yj] = ring[j] as [number, number];
      if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };
  if (!rings[0] || !inRing(rings[0])) return false;
  return !rings.slice(1).some(inRing);
}

/** ISO-8601 duration in an NWS validTime ("PT6H", "P1DT3H") → hours. */
export function durationHours(d: string): number {
  const m = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/.exec(d);
  if (!m) return 1;
  return Number(m[1] ?? 0) * 24 + Number(m[2] ?? 0) + Math.round(Number(m[3] ?? 0) / 60);
}

/**
 * Expand an NWS gridpoint series ({ validTime: "start/PTnH", value }) into hourly
 * values for the given hour starts. `spread` divides amounts (precipitation,
 * snow) evenly over their interval; levels (temperature) repeat.
 */
export function expandSeries(values: { validTime: string; value: number | null }[] | undefined, hours: number[], spread: boolean): (number | null)[] {
  const out: (number | null)[] = hours.map(() => null);
  if (!values) return out;
  const index = new Map(hours.map((h, i) => [h, i]));
  for (const v of values) {
    const [start, dur] = v.validTime.split("/");
    const t0 = Date.parse(start!);
    const n = Math.max(1, durationHours(dur ?? "PT1H"));
    for (let k = 0; k < n; k++) {
      const i = index.get(t0 + k * 3_600_000);
      if (i !== undefined) out[i] = v.value === null ? null : spread ? v.value / n : v.value;
    }
  }
  return out;
}

/** "Rising", "falling" or "steady" from a 3-hour pressure change (hPa). */
export function tendencyOf(changeHpa: number | null): ObsDetail["tendency"] {
  if (changeHpa === null) return null;
  if (changeHpa >= 1) return "rising";
  if (changeHpa <= -1) return "falling";
  return "steady";
}

/** Order and keep the useful parts of an Area Forecast Discussion. */
export function parseDiscussion(text: string): DiscussionSection[] {
  const sections: { title: string; lines: string[] }[] = [];
  let current: { title: string; lines: string[] } | null = null;
  for (const raw of text.replace(/\r/g, "").split("\n")) {
    const line = raw.trimEnd();
    // ".SHORT TERM /This evening through Thursday/...Text may start here"
    const head = /^\.([A-Z][A-Za-z0-9 &,'()-]*?)\s*(?:\/[^/]*\/)?\s*\.\.\.(.*)$/.exec(line);
    if (head) {
      current = { title: head[1]!.trim(), lines: head[2]!.trim() ? [head[2]!.trim()] : [] };
      sections.push(current);
      continue;
    }
    if (line === "&&" || line.startsWith("$$")) {
      current = null;
      continue;
    }
    current?.lines.push(line);
  }
  const rank = (t: string) => {
    const u = t.toUpperCase();
    if (u.startsWith("KEY MESSAGES")) return 0;
    if (u.startsWith("UPDATE")) return 1;
    if (u.startsWith("SYNOPSIS")) return 2;
    if (u.startsWith("DISCUSSION") || u.startsWith("SHORT TERM")) return 3;
    if (u.startsWith("LONG TERM")) return 4;
    if (u.startsWith("FIRE WEATHER")) return 5;
    if (u.startsWith("HYDROLOGY")) return 6;
    if (u.startsWith("AVIATION")) return 8;
    return 7;
  };
  return sections
    .filter((sec) => !/WATCHES|WARNINGS|ADVISORIES/i.test(sec.title))
    .map((sec) => ({
      title: titleCase(sec.title),
      text: sec.lines
        .join("\n")
        .split(/\n\s*\n/)
        .map((p) => p.replace(/\s*\n\s*/g, " ").trim())
        .filter(Boolean)
        .join("\n\n"),
    }))
    .filter((sec) => sec.text)
    .sort((a, b) => rank(a.title) - rank(b.title));
}

function titleCase(s: string): string {
  return s.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
}
